import { DEMO_PROJECTS } from './demo-data';
import {
  BRIEF_OPTIONS,
  type BriefOptionsMap,
  type MultiBriefPref,
  isBriefReady,
  isMultiBriefKey,
  nextBriefKey,
  nextDeepeningKey,
} from './brief-options';
import { recommendationFollowUp, messagesForTool } from './tool-actions';
import type { ChatMessage, PrefKey, Preferences } from './types';

let msgCounter = 0;
export function nextId() {
  return `msg-${++msgCounter}`;
}

export const INITIAL_MESSAGES: ChatMessage[] = [
  {
    id: nextId(),
    role: 'bot',
    text: 'Find Bangalore homes privately — no phone number until you choose to reveal.\n\nTell me what you\'re looking for, or tap a chip to start your brief.',
    quickReplies: (BRIEF_OPTIONS.purpose ?? []).map((value) => ({ pref: 'purpose', value })),
  },
];

export function recommendationMessages(_budget: string): ChatMessage[] {
  return [
    {
      id: nextId(),
      role: 'bot',
      text: 'Based on your brief, three projects stand out — ranked for budget fit, commute, and possession. Tap **View plans** on any card for the floor sheet and unit picker.',
      projects: [DEMO_PROJECTS.utopia!, DEMO_PROJECTS.ayana!, DEMO_PROJECTS.krishnaja!],
      showCompare: true,
      showVisitPlan: true,
    },
    recommendationFollowUp(),
  ];
}

const PREF_PROMPTS: Partial<Record<PrefKey, string>> = {
  budget: "Good — so I don't waste your weekends: what's the ceiling you'd rather stay under?",
  goal: "And what's the money's job here — growth, income, or keeping it safe?",
  propertyType: 'What kind of home are you picturing?',
  bhk: 'How much space does your family need?',
  plotSize: 'What size of land are you thinking?',
  location: 'Where would you like your life to be — which side of the city?',
  timeline: 'When are you looking to move?',
  worries:
    "Last one, and it's the question most portals never ask — what quietly worries you about this purchase? I rank homes around this.",
  horizon: "How long should the money stay in before you'd want it back — a few years, or a long hold?",
  schools:
    'One more, only if it matters to you — should I weigh school access? Some of the best-value homes sit a few minutes further out.',
  hub: "And if a daily commute is driving this, tell me where you work — I'll rank by real drive time, not distance.",
};

export function nextPrefKey(
  prefs: Preferences,
  options: BriefOptionsMap = BRIEF_OPTIONS,
): PrefKey | null {
  return nextBriefKey(prefs, options);
}

export function multiPrefSummary(prefs: Preferences, pref: MultiBriefPref): string {
  if (pref === 'bhk') return prefs.bhkSelections.join(', ');
  if (pref === 'plotSize') return prefs.plotSizeSelections.join(', ');
  if (pref === 'worries') return prefs.worries.join(', ');
  return prefs.propertyTypes.join(', ');
}

export function prefAckMessages(
  pref: PrefKey,
  value: string,
  prefs: Preferences,
  briefOptions: BriefOptionsMap = BRIEF_OPTIONS,
): ChatMessage[] {
  const messages: ChatMessage[] = [{ id: nextId(), role: 'user', text: value }];

  if (isBriefReady(prefs, briefOptions)) {
    const deepen = nextDeepeningKey(prefs, briefOptions);
    if (deepen) {
      messages.push({
        id: nextId(),
        role: 'bot',
        text: PREF_PROMPTS[deepen] ?? '',
        quickReplies: (briefOptions[deepen] ?? []).map((v) => ({ pref: deepen, value: v })),
      });
      return messages;
    }
    return [...messages, ...recommendationMessages(prefs.budget ?? '')];
  }

  const nextKey = nextPrefKey(prefs, briefOptions);
  if (!nextKey) return messages;

  const prompt = PREF_PROMPTS[nextKey] ?? `What about ${nextKey}?`;
  const chipOptions = briefOptions[nextKey] ?? [];

  messages.push({
    id: nextId(),
    role: 'bot',
    text: prompt,
    ...(isMultiBriefKey(nextKey)
      ? {}
      : { quickReplies: chipOptions.map((v) => ({ pref: nextKey, value: v })) }),
  });

  return messages;
}

/** Panel-driven pref update — returns new bot messages when brief completes. */
export function messagesAfterPanelPref(
  pref: PrefKey,
  prefs: Preferences,
  alreadyHasRecommendations: boolean,
  briefOptions: BriefOptionsMap = BRIEF_OPTIONS,
): ChatMessage[] {
  if (alreadyHasRecommendations || !isBriefReady(prefs, briefOptions)) return [];
  if (pref === 'location' || (pref === 'goal' && isBriefReady(prefs, briefOptions))) {
    return recommendationMessages(prefs.budget ?? '');
  }
  return [];
}

export function compareMessage(): ChatMessage {
  return {
    id: nextId(),
    role: 'bot',
    text: 'Side-by-side on price and possession — plus trend comparison for investment context:',
    showCompare: true,
    toolBlocks: [{ kind: 'compare_trends', title: 'Trend comparison' }],
  };
}

export function unitShortlistAddedMessage(
  projectName: string,
  unitNo: string,
  count: number,
): ChatMessage {
  return {
    id: nextId(),
    role: 'bot',
    text:
      count >= 2
        ? `**${projectName} · Unit ${unitNo}** saved. You have ${count} units shortlisted — compare or plan a visit day.`
        : `**${projectName} · Unit ${unitNo}** saved with full specs and pricing. Add more units or projects to compare.`,
    showVisitPlan: count >= 2,
  };
}

export function shortlistAddedMessage(name: string, count: number): ChatMessage {
  return {
    id: nextId(),
    role: 'bot',
    text:
      count >= 2
        ? `**${name}** is on your shortlist. You can plan a Saturday route whenever you're ready.`
        : `**${name}** saved. Add one more to compare or build a visit day.`,
    showVisitPlan: count >= 2,
  };
}

export function revealConfirmedMessage(input?: {
  builder?: string;
  project?: string;
  time?: string;
}): ChatMessage {
  const builder = input?.builder?.trim() || 'the builder';
  const project = input?.project?.trim() || 'your visit';
  const time = input?.time?.trim();
  const when = time ? ` for ${time}` : '';
  return {
    id: nextId(),
    role: 'bot',
    text: `Contact shared with ${builder} for ${project}${when} only. They'll confirm the slot on WhatsApp — no other builder was notified.`,
    visitConfirmed: { project, time: time || 'Visit day' },
    showConcierge: true,
  };
}

export function freeformReply(text: string): ChatMessage[] {
  const lower = text.toLowerCase();
  const projectMatch =
    (lower.includes('ayana') && 'ayana') ||
    (lower.includes('buena') && 'buena') ||
    (lower.includes('krishnaja') && 'krishnaja') ||
    (lower.includes('utopia') && 'utopia') ||
    undefined;

  if (/floor|layout|plan|2bhk|3d/.test(lower)) {
    return messagesForTool(projectMatch ?? 'ayana', 'floor_plan');
  }
  if (/legal|rera|title|history|litigation|ec |khata/.test(lower)) {
    return messagesForTool(projectMatch ?? 'ayana', 'legal_brief');
  }
  if (/trend|cagr|appreciation|yield|market|invest/.test(lower) && /compar|all|three|vs/.test(lower)) {
    return messagesForTool(undefined, 'compare_trends');
  }
  if (/trend|cagr|appreciation|yield|market/.test(lower)) {
    return messagesForTool(projectMatch ?? 'ayana', 'market_trends');
  }
  if (/route|map|direction|drive|visit route|saturday/.test(lower)) {
    return messagesForTool(undefined, 'visit_route');
  }

  if (!text.trim()) {
    return [{ id: nextId(), role: 'bot', text: 'Ask about floor plans, legal pack, area trends, or visit routing — or finish your brief for matches.' }];
  }
  return [{
    id: nextId(),
    role: 'bot',
    text: 'Try: "Show Ayana 2BHK floor plan", "Legal history for Buena Vista", "Compare area trends", or "Route for Saturday visits".',
  }];
}

export const PREF_LABELS: Record<PrefKey, string> = {
  purpose: 'Purpose',
  budget: 'Budget',
  bhk: 'Configuration',
  plotSize: 'Plot size',
  propertyType: 'Property type',
  location: 'Location',
  timeline: 'Timeline',
  goal: 'Investment goal',
  worries: 'Worries',
  horizon: 'Horizon',
  schools: 'Schools',
  hub: 'Commute hub',
};

export function applyPref(prefs: Preferences, pref: PrefKey, value: string): Preferences {
  const next = { ...prefs, [pref]: value };
  if (pref === 'purpose' && value !== 'Investment') {
    next.goal = null;
    next.horizon = null;
  }
  if (pref === 'location' && !next.timeline) {
    next.timeline = 'Next 6 months';
  }
  return next;
}

export function applyMultiPref(
  prefs: Preferences,
  pref: MultiBriefPref,
  values: string[],
): Preferences {
  // NEVER confirms: its only caller is toggleMultiPrefValue (chip taps).
  // Confirmation is exclusively confirmMultiPrefStep's job — the previous
  // auto-confirm here re-created the very auto-advance bug #16 fixed.
  if (pref === 'bhk') {
    return { ...prefs, bhkSelections: values, bhk: values.join(' · ') || null };
  }
  if (pref === 'plotSize') {
    return { ...prefs, plotSizeSelections: values };
  }
  if (pref === 'worries') {
    return { ...prefs, worries: values };
  }
  return { ...prefs, propertyTypes: values };
}

export function toggleMultiPrefValue(
  prefs: Preferences,
  pref: MultiBriefPref,
  value: string,
): Preferences {
  const current =
    pref === 'bhk'
      ? prefs.bhkSelections
      : pref === 'plotSize'
        ? prefs.plotSizeSelections
        : pref === 'worries'
          ? prefs.worries
          : prefs.propertyTypes;
  const nextValues = current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
  return applyMultiPref(prefs, pref, nextValues);
}

export function prefAckAfterMultiStep(
  pref: MultiBriefPref,
  prefs: Preferences,
  briefOptions: BriefOptionsMap = BRIEF_OPTIONS,
): ChatMessage[] {
  const summary = multiPrefSummary(prefs, pref);
  const messages: ChatMessage[] = [{ id: nextId(), role: 'user', text: summary }];

  if (isBriefReady(prefs, briefOptions)) {
    const deepen = nextDeepeningKey(prefs, briefOptions);
    if (deepen) {
      messages.push({
        id: nextId(),
        role: 'bot',
        text: PREF_PROMPTS[deepen] ?? '',
        quickReplies: (briefOptions[deepen] ?? []).map((v) => ({ pref: deepen, value: v })),
      });
    }
    return messages;
  }

  const nextKey = nextPrefKey(prefs, briefOptions);
  if (!nextKey) return messages;

  const prompt = PREF_PROMPTS[nextKey] ?? `What about ${nextKey}?`;
  const chipOptions = briefOptions[nextKey] ?? [];
  messages.push({
    id: nextId(),
    role: 'bot',
    text: prompt,
    ...(isMultiBriefKey(nextKey)
      ? {}
      : { quickReplies: chipOptions.map((v) => ({ pref: nextKey, value: v })) }),
  });
  return messages;
}

/** Legacy chip path — kept for any inline quick replies still in thread. */
export function botResponse(
  pref: PrefKey,
  value: string,
  prefs: Preferences,
): ChatMessage[] {
  return messagesAfterPanelPref(pref, prefs, false);
}
